import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import type {
  Expense,
  ExpenseCategory,
} from '@pflegenavi/frontend/api-nursing-home';
import {
  EXPENSE_CATEGORIES,
  useDeleteExpense,
  useUpdateExpense,
} from '@pflegenavi/frontend/api-nursing-home';
import { useSnackbar } from 'notistack';
import {
  DeleteDialog,
  Typography,
  useErrorSnackbar,
} from '@pflegenavi/web-components';
import { format, parseISO } from 'date-fns';
import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  UploadReceiptCard,
  useUploadReceipt,
} from '@pflegenavi/transaction-components';
import type { Employee } from '@pflegenavi/shared/api';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { YEAR_SHORTMONTH_DAY_FORMAT } from '@pflegenavi/shared/constants';

type ExpenseFieldName =
  | 'receipt_date'
  | 'receipt_number'
  | 'receipt_image_ids'
  | 'receipt_not_available'
  | 'category'
  | 'comment';

interface FormValues {
  receiptImageIds: string[];
  receipt_not_available: boolean;
  receiptDate: Date | null;
  receiptNumber: string;
  category: ExpenseCategory | '';
  comment: string;
}

const validationSchema = Yup.object({
  receiptImageIds: Yup.array().of(Yup.string().required()),
  receipt_not_available: Yup.boolean().required(),
  receiptDate: Yup.date().nullable(),
  receiptNumber: Yup.string(),
  category: Yup.string(),
  comment: Yup.string(),
});

export interface EditExpenseDialogProps {
  open: boolean;
  onClose: () => void;
  expense: Expense | null;
  employees: Employee[] | undefined;
}

type DialogMode = 'open' | 'scheduled' | 'exported';

function getMode(expense: Expense | null): DialogMode {
  if (!expense) {
    return 'open';
  }
  if (expense.expense_export_id) {
    return 'exported';
  }
  if (expense.scheduled_for_export_at) {
    return 'scheduled';
  }
  return 'open';
}

export const EditExpenseDialog: FC<EditExpenseDialogProps> = ({
  open,
  onClose,
  expense,
  employees,
}) => {
  const { t } = useTranslation();
  const fDate = useFormatDate();
  const { fCurrencyCents } = useFormatting();
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const { mutateAsync: updateExpense } = useUpdateExpense();
  const { mutateAsync: deleteExpense } = useDeleteExpense();

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const mode = getMode(expense);
  const readOnly = mode === 'exported';

  const categoryLocked = Boolean(
    expense?.cash_transaction_group?.pliant_transaction_categories?.length
  );

  const initialValues = useMemo<FormValues>(
    () => ({
      receiptImageIds: expense?.receipt_image_ids ?? [],
      receipt_not_available: expense?.receipt_not_available ?? false,
      receiptDate: expense?.receipt_date
        ? parseISO(expense.receipt_date)
        : null,
      receiptNumber: expense?.receipt_number ?? '',
      category: expense?.category ?? '',
      comment: expense?.comment ?? '',
    }),
    [expense]
  );

  const formik = useFormik<FormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      if (!expense) {
        return;
      }
      const hasImages = values.receiptImageIds.length > 0;
      try {
        await updateExpense({
          params: {
            expense_id: expense.id,
          },
          body: {
            category: values.category === '' ? null : values.category,
            comment:
              values.comment.trim() === '' ? null : values.comment.trim(),
            receipt_date: values.receiptDate
              ? format(values.receiptDate, 'yyyy-MM-dd')
              : null,
            receipt_number:
              values.receiptNumber.trim() === ''
                ? null
                : values.receiptNumber.trim(),
            receipt_image_ids: values.receiptImageIds,
            receipt_not_available: hasImages
              ? false
              : values.receipt_not_available,
          },
        });

        enqueueSnackbar(t('accounting.expenses.dialog.save-success'), {
          variant: 'success',
        });
        onClose();
      } catch (error) {
        const fieldErrors = extractFieldErrors(error);
        if (fieldErrors) {
          for (const [field, message] of Object.entries(fieldErrors)) {
            const formField = mapBackendFieldToFormField(field);
            if (formField) {
              setFieldError(formField, message);
            }
          }
        }
        enqueueErrorSnackbar(error, t('accounting.expenses.dialog.save-error'));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const uploadReceipt = useUploadReceipt({ formik });

  // Keep the "no receipt available" checkbox and uploaded images mutually
  // exclusive: as soon as an image lands, drop the not-available flag.
  const { setFieldValue } = formik;
  const hasImages = formik.values.receiptImageIds.length > 0;
  const notAvailable = formik.values.receipt_not_available;
  useEffect(() => {
    if (hasImages && notAvailable) {
      setFieldValue('receipt_not_available', false);
    }
  }, [hasImages, notAvailable, setFieldValue]);

  const handleDelete = async () => {
    if (!expense) {
      return;
    }
    try {
      await deleteExpense({
        params: {
          expense_id: expense.id,
          cash_list_transaction_group_id:
            expense.cash_list_transaction_group_id,
        },
      });
      enqueueSnackbar(t('accounting.expenses.dialog.delete-success'), {
        variant: 'success',
      });
      setConfirmDeleteOpen(false);
      onClose();
    } catch (error) {
      enqueueErrorSnackbar(error, t('accounting.expenses.dialog.delete-error'));
    }
  };

  const employeeName = useMemo(() => {
    if (!expense || !employees) {
      return '--';
    }
    const employee = employees.find((e) => e.id === expense.inserted_by);
    return employee ? `${employee.first_name} ${employee.last_name}` : '--';
  }, [expense, employees]);

  if (!expense) {
    return null;
  }

  const groupAmount = expense.expense_parts.reduce(
    (sum, p) => sum + p.amount,
    0
  );

  const title =
    mode === 'exported'
      ? t('accounting.expenses.dialog.title.exported')
      : t('accounting.expenses.dialog.title');

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          {mode === 'exported' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('accounting.expenses.dialog.exported-banner')}
            </Alert>
          )}

          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <ContextRow
              label={t('accounting.expenses.dialog.context.cash-list')}
              value={expense.cash_transaction_group?.cash_list_name ?? '--'}
            />
            <ContextRow
              label={t('accounting.expenses.dialog.context.amount')}
              value={fCurrencyCents(groupAmount)}
            />
            <ContextRow
              label={t('accounting.expenses.dialog.context.merchant')}
              value={expense.merchant_name ?? '--'}
            />
            <ContextRow
              label={t('accounting.expenses.dialog.context.created-by')}
              value={`${employeeName} · ${fDate(
                new Date(expense.inserted_at),
                YEAR_SHORTMONTH_DAY_FORMAT
              )}`}
            />
            {mode === 'exported' && expense.expense_export?.inserted_at && (
              <ContextRow
                label={t('accounting.expenses.dialog.context.exported-on')}
                value={fDate(
                  new Date(expense.expense_export.inserted_at),
                  YEAR_SHORTMONTH_DAY_FORMAT
                )}
              />
            )}
          </Stack>
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2}>
            <DatePicker
              label={t('accounting.expenses.dialog.field.receipt-date')}
              value={formik.values.receiptDate}
              onChange={(value) =>
                formik.setFieldValue('receiptDate', value ?? null)
              }
              disabled={readOnly}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: Boolean(formik.errors.receiptDate),
                  helperText: formik.errors.receiptDate,
                },
              }}
            />

            <TextField
              label={t('accounting.expenses.dialog.field.receipt-number')}
              value={formik.values.receiptNumber}
              onChange={(e) =>
                formik.setFieldValue('receiptNumber', e.target.value)
              }
              disabled={readOnly}
              error={Boolean(formik.errors.receiptNumber)}
              helperText={formik.errors.receiptNumber}
              fullWidth
            />

            <FormControl
              fullWidth
              error={Boolean(formik.errors.category)}
              disabled={readOnly || categoryLocked}
            >
              <InputLabel id="edit-expense-category-label">
                {t('accounting.expenses.dialog.field.category')}
              </InputLabel>
              <Select
                labelId="edit-expense-category-label"
                value={formik.values.category}
                label={t('accounting.expenses.dialog.field.category')}
                onChange={(e) =>
                  formik.setFieldValue(
                    'category',
                    e.target.value as ExpenseCategory | ''
                  )
                }
              >
                <MenuItem value="">
                  {t('accounting.expenses.dialog.field.category-empty')}
                </MenuItem>
                {EXPENSE_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {t(`accounting.expenses.category.${c}`)}
                  </MenuItem>
                ))}
              </Select>
              {categoryLocked && (
                <Typography variant="caption" color="grey.600" sx={{ mt: 0.5 }}>
                  {t('accounting.expenses.dialog.category-locked-tooltip')}
                </Typography>
              )}
              {formik.errors.category && (
                <Typography
                  variant="caption"
                  color="error.main"
                  sx={{ mt: 0.5 }}
                >
                  {formik.errors.category}
                </Typography>
              )}
            </FormControl>

            <UploadReceiptCard
              formik={formik}
              uploadReceipt={uploadReceipt}
              disabled={readOnly}
              allowWithoutImage
            />

            <TextField
              label={t('accounting.expenses.dialog.field.comment')}
              value={formik.values.comment}
              onChange={(e) => formik.setFieldValue('comment', e.target.value)}
              disabled={readOnly}
              error={Boolean(formik.errors.comment)}
              helperText={formik.errors.comment}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          {mode === 'open' && (
            <Button
              onClick={() => setConfirmDeleteOpen(true)}
              color="error"
              disabled={formik.isSubmitting}
              sx={{ mr: 'auto' }}
            >
              {t('accounting.expenses.dialog.delete')}
            </Button>
          )}
          <Button
            onClick={onClose}
            color="inherit"
            disabled={formik.isSubmitting}
          >
            {readOnly ? t('actions.close') : t('actions.cancel')}
          </Button>
          {!readOnly && (
            <Button
              onClick={() => formik.handleSubmit()}
              color="primary"
              variant="contained"
              disabled={formik.isSubmitting}
            >
              {t('actions.save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <DeleteDialog
        open={confirmDeleteOpen}
        setDeleteDialogOpen={setConfirmDeleteOpen}
        handleDelete={handleDelete}
        title={t('accounting.expenses.dialog.delete-confirm.title')}
        confirmationMessage={t(
          'accounting.expenses.dialog.delete-confirm.body'
        )}
        deleteButtonMessage={t('accounting.expenses.dialog.delete')}
      />
    </>
  );
};

interface ContextRowProps {
  label: string;
  value: React.ReactNode;
}

const ContextRow: FC<ContextRowProps> = ({ label, value }) => (
  <Stack direction="row" spacing={1} alignItems="baseline">
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Stack>
);

function extractFieldErrors(error: unknown): Record<string, string> | null {
  if (!error || typeof error !== 'object') {
    return null;
  }
  // ClientError flattens response body keys onto itself; 422 changeset shape is
  // `{ errors: { field: ["msg", ...] } }`.
  const errors = (error as { errors?: unknown }).errors;
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return null;
  }
  const result: Record<string, string> = {};
  for (const [field, messages] of Object.entries(errors)) {
    if (field === 'detail') {
      continue;
    }
    if (Array.isArray(messages) && messages.length > 0) {
      result[field] = String(messages[0]);
    } else if (typeof messages === 'string') {
      result[field] = messages;
    }
  }
  return Object.keys(result).length > 0 ? result : null;
}

function mapBackendFieldToFormField(field: string): keyof FormValues | null {
  switch (field as ExpenseFieldName) {
    case 'receipt_date':
      return 'receiptDate';
    case 'receipt_number':
      return 'receiptNumber';
    case 'receipt_image_ids':
      return 'receiptImageIds';
    case 'receipt_not_available':
      return 'receipt_not_available';
    case 'category':
      return 'category';
    case 'comment':
      return 'comment';
    default:
      return null;
  }
}
